import { CONFLICT_GROUP, DEFAULT_BINDINGS, OPTIONAL_BINDINGS, type ActionBinding } from './bindings';

// D127 — ONE table describing every rebindable action.
//
// Before this, the same information lived in three places that could disagree: `DEFAULT_BINDINGS` (the
// map), `keyboardInput`/`main.ts` (the six hard-coded keys that were never in the map at all), and
// `cheatSheet.ts` (a literal list of rows, including six that lied about keys nobody could change).
// The controls screen, the cheat sheet and the input layer now all read this.

// D137 (§7.2): the groups ARE the user's taxonomy — universal verbs work at any time ("because all
// those actions must be possible also when a ball is in-flight"), the defensive ones need an opponent
// on the ball, and the keeper's dives only exist while he faces a penalty.
export type ActionGroup = 'move' | 'universal' | 'defend' | 'keeper' | 'meta';

export interface ActionDef {
  readonly id: ActionBinding;
  readonly label: string;
  readonly group: ActionGroup;
  readonly defaultCode: string;
  /** May be left unbound (shown as "—" and clearable with Delete). */
  readonly optional: boolean;
  /** D137: when this action shares its key with another, the context that tells them apart. */
  readonly context?: string;
  /** Extra keys that always work regardless of the binding — the arrow keys, so they never go missing. */
  readonly aliases?: readonly string[];
}

export const GROUP_LABEL: Readonly<Record<ActionGroup, string>> = {
  move: 'Movement',
  universal: 'Always available',
  defend: 'When they have the ball',
  keeper: 'Goalkeeper (penalties)',
  meta: 'General',
};

const def = (id: ActionBinding, label: string, group: ActionGroup, opts: { context?: string; aliases?: readonly string[] } = {}): ActionDef => ({
  id,
  label,
  group,
  defaultCode: DEFAULT_BINDINGS[id],
  optional: OPTIONAL_BINDINGS.has(id),
  ...(opts.context ? { context: opts.context } : {}),
  ...(opts.aliases ? { aliases: opts.aliases } : {}),
});

/** Display order — this is the order the controls screen and the cheat sheet render. */
export const ACTION_DEFS: readonly ActionDef[] = [
  def('moveUp', 'Run forward', 'move', { aliases: ['ArrowUp'] }),
  def('moveDown', 'Run back', 'move', { aliases: ['ArrowDown'] }),
  def('moveLeft', 'Run left', 'move', { aliases: ['ArrowLeft'] }),
  def('moveRight', 'Run right', 'move', { aliases: ['ArrowRight'] }),
  def('sprint', 'Sprint (hold)', 'move'),

  def('shoot', 'Shoot — hold to aim, release to fire', 'universal'),
  def('passDirect', 'Pass', 'universal', { context: 'when the ball is not a teammate’s' }),
  def('passLong', 'Long pass', 'universal'),
  def('loft', 'Loft (hold) · lob (tap) · higher lob (double tap)', 'universal', { context: 'when they do not have the ball' }),
  def('call', 'Call for it', 'universal', { context: 'when a teammate has the ball' }),
  def('shield', 'Shield the ball (hold)', 'universal', { context: 'while you have the ball' }),
  def('diveFlop', 'Dive / go down', 'universal'),
  def('punch', 'Punch (the ref is watching)', 'universal'),

  def('tackle', 'Tackle — safe, never a foul', 'defend'),
  def('tackleHard', 'Hard tackle — wins through contact, can foul', 'defend', { context: 'while they have the ball' }),
  def('slide', 'Slide tackle', 'defend'),
  def('block', 'Block stance (hold)', 'defend', { context: 'while you do not have the ball' }),
  def('shove', 'Shoulder barge', 'defend'),

  def('gkDiveL', 'Dive left', 'keeper', { context: 'defending a penalty' }),
  def('gkDiveR', 'Dive right', 'keeper', { context: 'defending a penalty' }),

  def('switchPlayer', 'Switch player', 'meta'),
  def('cheatSheet', 'Controls overlay', 'meta'),
];

/** Fixed keys the player cannot rebind — shown for completeness, never captured. */
export const FIXED_KEYS: readonly { readonly label: string; readonly code: string }[] = [
  { label: 'Pause / release mouse', code: 'Escape' },
  { label: 'Export debug clip', code: 'F8' },
];

/** Human-readable name for a KeyboardEvent.code / mouse pseudo-code. '' renders as unbound. */
export function keyLabel(code: string): string {
  if (!code) return '—';
  if (code === 'Mouse0') return 'Left click';
  if (code === 'Mouse1') return 'Middle click';
  if (code === 'Mouse2') return 'Right click';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return `${code.slice(5)} arrow`;
  if (code === 'ShiftLeft' || code === 'ShiftRight') return 'Shift';
  if (code === 'ControlLeft' || code === 'ControlRight') return 'Ctrl';
  if (code === 'AltLeft' || code === 'AltRight') return 'Alt';
  return code;
}

/**
 * The action currently bound to `code`, or null. Used for conflict detection (D127: conflict = swap).
 *
 * D137 — a shared key is only a CONFLICT across context groups. Space genuinely means both "pass" and
 * "call for it", E means loft, hard tackle and the keeper's right-hand dive; swapping those apart would
 * dismantle the layout the moment anyone opened the controls screen and re-bound one of them.
 */
export function actionUsing(bindings: Readonly<Record<ActionBinding, string>>, code: string, except: ActionBinding): ActionBinding | null {
  if (!code) return null;
  const group = CONFLICT_GROUP[except];
  for (const d of ACTION_DEFS) {
    if (d.id === except || bindings[d.id] !== code) continue;
    if (group !== undefined && CONFLICT_GROUP[d.id] === group) continue; // same context ⇒ not a clash
    return d.id;
  }
  return null;
}
